// Promise.withResolvers() is ES2024, above our supported-browser floor
// (es-check gate: es2022 / Safari 15.4). pdfjs-dist 4.x uses it on the main
// thread and in its worker. Our own code can't reach it — tsconfig `lib` is
// es2022 — but the bundled dependency can, and es-check flags the feature
// even when the same file polyfills it first. This install-if-missing shim
// makes the main-thread call safe. The pdf.js worker is a separate realm and
// does not see this polyfill — we load a matching modern worker from an
// origin-absolute URL (see pdfPreviewWorker.ts) so current Chrome/Safari work.
//
// The `'withResolvers' in Promise` guard (rather than a property access)
// keeps our own bundle free of a `Promise.withResolvers` member expression
// that the es-check feature detector would flag on this file.
type WithResolversFn = <T>() => {
  promise: Promise<T>
  resolve: (value: T | PromiseLike<T>) => void
  reject: (reason?: unknown) => void
}

export const promiseWithResolversPolyfill: WithResolversFn = function <T>() {
  let resolve!: (value: T | PromiseLike<T>) => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })
  return { promise, resolve, reject }
}

if (!('withResolvers' in Promise)) {
  console.warn(
    'Promise.withResolvers is missing in this environment — installing a minimal polyfill'
  )
  Object.defineProperty(Promise, 'withResolvers', {
    value: promiseWithResolversPolyfill,
    writable: true,
    configurable: true,
    enumerable: false,
  })
}
